import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import qs from 'qs';
import { useReactToPrint } from 'react-to-print';

import { Paper, Grid, Typography } from '@material-ui/core';
import { useFormik } from 'formik';
import Filter from 'components/Filter/components';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Button from 'components/Button';
import VehicleDamageExport from './Export';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import { loadVehicleDamageList } from '../store/thunk';
import { handleExporting, handleSearch } from 'components/Filter/store/actions';

import { inputs } from './resources';
import { formatVehicle } from 'helpers/formatters';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

export default function VehicleDamage() {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { isSearching, isExporting } = useSelector(state => state.filters);
  const { list, isLoading } = useSelector(state => state.vehicleDamage);

  const [qp, setQp] = useState('');
  const [exportData, setExportData] = useState({});

  const contentRef = useRef(null);
  const reactToPrint = useReactToPrint({ contentRef, documentTitle: 'dossie-de-avarias', preserveAfterPrint: true });

  const requestVehicleDamageList = qp => {
    setQp(qp);
    dispatch(loadVehicleDamageList({ userId, establishmentId, isExport: false, qp }));
  }

  const handleLoadVehicleDamageExport = async qp => {
    dispatch(handleExporting(true));

    await dispatch(loadVehicleDamageList({ userId, establishmentId, isExport: true, qp }))
      .then(data => setExportData(data))
      .catch(() => setExportData({}))
      .finally(() => dispatch(handleExporting(false)));
  }

  useEffect(() => {
    requestVehicleDamageList(qp);
  }, [establishmentId]);

  const handleFilterSubmit = values => {
    const formattedParams = Object.fromEntries(Object.entries(values).filter(([_, value]) => value));
    const params = `&${qs.stringify(formattedParams)}`;

    setQp(params);
    dispatch(handleSearch(true));

    requestVehicleDamageList(params);
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      plate: '',
      serviceOrderCode: ''
    },
    onSubmit: values => handleFilterSubmit(values)
  });

  return(
    <Paper>
      <div style={{ display: 'none' }}>
        <VehicleDamageExport
          ref={contentRef}
          data={exportData}
        />
      </div>
      {isMobile && (
        <form
          onSubmit={formik.handleSubmit}
          style={{ padding: 10 }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Input
                name='plate'
                label='Placa'
                value={formik.values.plate}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                name='serviceOrderCode'
                label='Código da OS'
                value={formik.values.serviceOrderCode}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                type='submit'
                color='success'
              >
                Buscar Avarias
              </Button>
            </Grid>
            {(list?.length > 0) && (
              <Grid item xs={12} style={{ marginTop: 10 }}>
                <Button
                  fullWidth
                  color='tertiary'
                  variant='outlined'
                  loading={isExporting}
                  onClick={() => {
                    handleLoadVehicleDamageExport(qp)
                      .then(() => reactToPrint());
                  }}
                  startIcon={
                    <FontAwesomeIcon
                      size='xs'
                      icon={faDownload}
                    />
                  }
                >
                  Exportar PDF
                </Button>
              </Grid>
            )}
          </Grid>
        </form>
      )}
      {!isMobile && (
        <Filter
          filter
          filters={inputs}
          isDisabledXlsExport
          vehicleDamageExport={list?.length > 0}
          vehicleDamageExportFunction={() => {
            handleLoadVehicleDamageExport(qp)
              .then(() => reactToPrint());
          }}
          handlePageRequest={({ qp }) => requestVehicleDamageList(qp)}
        />
      )}
      <Loader isLoading={isLoading}>
        <Grid container>
          {!isSearching && (
            <Grid xs={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', margin: 30 }}>
              <Typography>
                Pesquise por placa ou número da ordem de serviço para listar as avarias.
              </Typography>
            </Grid>
          )}
          {(isSearching && list?.length == 0) && (
            <Grid xs={12} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', margin: 30 }}>
              <Typography>
                Este carro ou OS não possuem avaria registrada no sistema.
              </Typography>
            </Grid>
          )}
          {isMobile && (
            <>
              {list?.map(item => {
                return(
                  <Grid xs={12} style={{ padding: 10 }}>
                    <Grid container style={{ backgroundColor: '#FCFCFC', border: '1px solid #D7D7D7', borderRadius: 5, padding: 10 }}>
                      <Grid item xs={4} style={{ height: 105 }}>
                        <img
                          src={item?.photoUrl}
                          style={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                            borderRadius: 5
                          }}
                        />
                      </Grid>
                      <Grid item xs={8} style={{ padding: 5 }}>
                        {!!item?.plate && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color='primary' variant='body2'>
                              <b>Placa</b>
                            </Typography>
                            <Typography variant='body2'>{item?.plate}</Typography>
                          </div>
                        )}
                        {!!item?.partName && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color='primary' variant='body2'>
                              <b>Área</b>
                            </Typography>
                            <Typography variant='body2'>{item?.partName}</Typography>
                          </div>
                        )}
                        {!!item?.typeName && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color='primary' variant='body2'>
                              <b>Tipo</b>
                            </Typography>
                            <Typography variant='body2'>{item?.typeName}</Typography>
                          </div>
                        )}
                        {!!item?.registerDateTime && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color='primary' variant='body2'>
                              <b>Data de criação</b>
                            </Typography>
                            <Typography variant='body2'>{moment(item?.registerDateTime).format('DD/MM/YYYY')}</Typography>
                          </div>
                        )}
                        {!!item?.registerDateTime && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography color='primary' variant='body2'>
                              <b>Horário</b>
                            </Typography>
                            <Typography variant='body2'>{moment(item?.registerDateTime).format('HH:mm:ss')}</Typography>
                          </div>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </>
          )}
          {!isMobile && (
            <>
              {list?.map(item => {
                return(
                  <Grid xs={12} md={6}>
                    <Grid container style={{ padding: 5 }}>
                      {!!item?.photoUrl ? (
                        <Grid item xs={6}>
                          <img
                            src={item?.photoUrl}
                            style={{
                              height: '100%',
                              width: 'stretch'
                            }}
                          />
                        </Grid>
                      ) : (
                        <Grid style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '50%', height: 'auto', border: 'solid 1px #505050' }}>
                          <Typography style={{ color: '#505050' }} variant='h3'>Sem Foto</Typography>
                        </Grid>
                      )}
                      <Grid item xs={6} style={{ backgroundColor: '#F3F3F3', border: 'solid 1px #E4E4E4', paddingLeft: 5 }}>
                        {!!item?.plate && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Placa: {item?.plate}</Typography>
                        )}
                        {!!item?.vehicleModel && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Veículo: {formatVehicle({ vehicleColor: item?.vehicleColor, vehicleModel: item?.vehicleModel })}</Typography>
                        )}
                        {!!item?.partName && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Área: {item?.partName}</Typography>
                        )}
                        {!!item?.categoryName && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Parte: {item?.categoryName}</Typography>
                        )}
                        {!!item?.typeName && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Tipo: {item?.typeName}</Typography>
                        )}
                        {!!item?.observation && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Observação: {item?.observation}</Typography>
                        )}
                        {!!item?.registerDateTime && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Data de Criação: {moment(item?.registerDateTime).format('DD/MM/YYYY')}</Typography>
                        )}
                        {!!item?.registerDateTime && (
                          <Typography style={{ margin: 0, color: '#505050' }}>Horário: {moment(item?.registerDateTime).format('HH:mm:ss')}</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </>
          )}
        </Grid>
      </Loader>
    </Paper>
  );
}